import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import type { PropsWithoutRef, Ref } from 'react';
import styled from 'styled-components';

import Icon, { registerIcon } from '../Icon';
import type { RefElement, RequireAtLeastOne, WithAttributes } from '../../types';
import HTML from '../HTML';
import Button from '../Button';
import { InfoDialog } from '../Dialog';
import type { DialogHandleValue } from '../Dialog';
import * as informationIcon from '../Icon/icons/information.icon';
import { useConsolidatedRef, useI18n } from '../../hooks';
import Flex from '../Flex';

registerIcon(informationIcon);

export type AdditionalInfoProps = WithAttributes<
  'div',
  RequireAtLeastOne<
    {
      heading?: string;
      children: string | JSX.Element;
      /** Used for the accessible button label, if not provided heading is used */
      contextualLabel?: string;
      dialogHandle?: Ref<DialogHandleValue>;
    },
    'heading' | 'contextualLabel'
  >
>;

export const StyledAdditionalInfo = styled.div``;

export default forwardRef<RefElement<AdditionalInfoProps>, PropsWithoutRef<AdditionalInfoProps>>(
  function AdditionalInfo(props, ref) {
    const t = useI18n();
    const {
      heading = t('additional_info'),
      contextualLabel,
      children,
      dialogHandle,
      ...restProps
    } = props;

    const [target, setTarget] = useState<HTMLButtonElement | null>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const handleRef = useConsolidatedRef(dialogHandle);

    useImperativeHandle(handleRef, () => {
      return {
        open: () => {
          setTarget(buttonRef.current);
        },
        close: () => {
          setTarget(null);
        }
      };
    });

    return (
      <Flex container as={StyledAdditionalInfo} ref={ref} {...restProps}>
        <Button
          variant='simple'
          label={t('additional_info')}
          compact
          icon
          ref={buttonRef}
          onClick={() => {
            handleRef.current?.open();
          }}
          aria-label={`${t('additional_info')} - ${contextualLabel ?? heading}`}
        >
          <Icon name='information' />
        </Button>
        {target && (
          <InfoDialog
            heading={heading}
            target={target}
            onDismiss={() => {
              handleRef.current?.close();
            }}
          >
            {typeof children === 'string' ? <HTML content={children} /> : children}
          </InfoDialog>
        )}
      </Flex>
    );
  }
);
